import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Table, TableColumnsType } from 'antd';
import Button from '../../Button';
import useNotification from '../../../hooks/useNotification';
import useAxios from '../../../hooks/useAxios';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import { ISemesterEvaluation } from '../../../models/semesterEvaluation.model';
import {
  deleteSemesterEvaluation,
  postSemesterEvaluation,
  putSemesterEvaluation,
} from '../../../api/semesterEvaluation.api';
import {
  IGetSemesterCourseSubjects,
  ISubject,
} from '../../../models/subject.model';
import { getSemesterCourseSubjects } from '../../../api/subject.api';
import { ISemesterLessonPlan } from '../../../models/semesterLessonPlan2.model';
import { getSemesterLessonPlans } from '../../../api/semesterLessonPlan2.api';
import _ from 'lodash';

interface IProps {
  semesterEvaluations: ISemesterEvaluation[];
  semesterCourseCode?: number;
  learningTypeCode?: string;
}

const EvaluationNewTable: FC<IProps> = ({
  semesterEvaluations,
  semesterCourseCode,
  learningTypeCode,
}) => {
  const { t } = useTranslation();
  const { successNotification } = useNotification();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetSemesterCourseSubjects>();
  const {
    arr: res3,
    isLoading: isLoading3,
    error: error3,
    executeArr: execute3,
  } = useAxios<any>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios<any>();
  const {
    data: deleteRes,
    isLoading: deleteIsLoading,
    error: deleteError,
    execute: deleteExecute,
  } = useAxios<any>();

  const [data, setData] = useState<ISemesterEvaluation[]>([]);
  const [subjectCodes, setSubjectCodes] = useState<(number | null)[]>([]);
  const [lessonPlans, setLessonPlans] = useState<ISemesterLessonPlan[][]>([]);
  const [lessonPlanCodes, setLessonPlanCodes] = useState<(number | null)[]>([]);

  useEffect(() => {
    if (semesterCourseCode) {
      execute2(getSemesterCourseSubjects({ semesterCourseCode }));
    }
  }, [semesterCourseCode]);

  useEffect(() => {
    if (semesterEvaluations) {
      setData(_.cloneDeep(semesterEvaluations));
      setSubjectCodes(semesterEvaluations.map((item) => item.subjectCode));
      setLessonPlanCodes(
        semesterEvaluations.map((item) => item.lessonPlanCode),
      );
    }
  }, [semesterEvaluations]);

  useEffect(() => {
    if (res2) {
      const arr: any = [];
      res2.data?.forEach((subject: ISubject) => {
        arr.push(
          getSemesterLessonPlans({
            semesterCourseCode: semesterCourseCode!,
            subjectCode: Number(subject.subjectCode),
          }),
        );
      });
      execute3(arr);
    }
  }, [res2]);

  useEffect(() => {
    if (res3) {
      setLessonPlans(res3.map((res) => res.data));
    }
  }, [res3]);

  useEffect(() => {
    if (deleteRes) {
      const newData = [...data];
      const targetItem = newData.find(
        (item) => item.evaluationCode === deleteRes.data?.evaluationCode,
      );
      if (targetItem) {
        targetItem.evaluationCode = null;
        const targetIndex = newData.indexOf(targetItem);

        const newSubjectCodes = [...subjectCodes];
        newSubjectCodes[targetIndex] = null;
        setSubjectCodes(newSubjectCodes);

        const newLessonPlanCodes = [...lessonPlanCodes];
        newLessonPlanCodes[targetIndex] = null;
        setLessonPlanCodes(newLessonPlanCodes);
      }
      setData(newData);
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes]);

  useEffect(() => {
    if (submitRes) {
      const newData = [...data];
      const targetItem = newData.find(
        (item) => item.prdCurriculumCode === submitRes.data?.prdCurriculumCode,
      );
      if (targetItem) {
        targetItem.evaluationCode = submitRes.data?.evaluationCode;
      }
      setData(newData);
      successNotification(t('student.student.message1'));
    }
  }, [submitRes]);

  const onSetSubjectCodes = (index: number, value: number) => {
    const newSubjectCodes = [...subjectCodes];
    newSubjectCodes[index] = value;
    setSubjectCodes(newSubjectCodes);
  };

  const onSetLessonPlanCodes = (index: number, value: number | null) => {
    const newLessonPlanCodes = [...lessonPlanCodes];
    newLessonPlanCodes[index] = value;
    setLessonPlanCodes(newLessonPlanCodes);
  };

  const getLessonPlans = (subjectCode: number | null) => {
    if (!subjectCode) return [];

    const subjectIndex = res2?.data?.findIndex(
      (item: ISubject) => Number(item.subjectCode) === subjectCode,
    );
    return typeof subjectIndex === 'number' ? lessonPlans[subjectIndex] : [];
  };

  const onDelete = (evaluationCode: number) => {
    deleteExecute(deleteSemesterEvaluation(evaluationCode));
  };

  const onSubmit = (record: ISemesterEvaluation, index: number) => {
    if (record.evaluationCode) {
      submitExecute(
        putSemesterEvaluation(record.evaluationCode, {
          prdCurriculumCode: record.prdCurriculumCode,
          evaluationType: learningTypeCode!,
          lessonPlanCode: lessonPlanCodes[index]!,
        }),
      );
    } else {
      submitExecute(
        postSemesterEvaluation({
          prdCurriculumCode: record.prdCurriculumCode,
          evaluationType: learningTypeCode!,
          lessonPlanCode: lessonPlanCodes[index]!,
        }),
      );
    }
  };

  const columns: TableColumnsType<ISemesterEvaluation> = [
    {
      title: t('program.semester.column29'),
      dataIndex: 'curriculumName',
    },
    {
      title: t('program.semester.5'),
      dataIndex: 'subjectCode',
      render: (value, record, index) => (
        <div className="w-50p">
          <Select
            className="w-100"
            placeholder={t('common.button.select')}
            value={subjectCodes[index]}
            onChange={(value) => {
              onSetSubjectCodes(index, value);
              onSetLessonPlanCodes(index, null);
            }}
          >
            {res2?.data?.map((item: ISubject) => (
              <Select.Option key={item.subjectCode} value={item.subjectCode}>
                {item.subjectName}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    },
    {
      title: t('program.semester.column23'),
      dataIndex: 'lessonPlanCode',
      render: (value, record, index) => (
        <div className="w-50p">
          <Select
            className="w-100"
            placeholder={t('common.button.select')}
            value={lessonPlanCodes[index]}
            onChange={(value) => onSetLessonPlanCodes(index, value)}
            disabled={!subjectCodes[index]}
          >
            {getLessonPlans(subjectCodes[index]).map(
              (item: ISemesterLessonPlan) => (
                <Select.Option
                  key={item.lessonPlanCode}
                  value={item.lessonPlanCode}
                >
                  {item.subjectSeq}
                </Select.Option>
              ),
            )}
          </Select>
        </div>
      ),
    },
    {
      title: '',
      key: 'evaluationCode',
      dataIndex: 'evaluationCode',
      align: 'center',
      render: (value) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small gray"
            onClick={() => onDelete(value)}
            text={t('common.button.delete')}
            disabled={!value}
          />
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'prdCurriculumCode',
      align: 'center',
      render: (value, record, index) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink"
            onClick={() => onSubmit(record, index)}
            text={t('common.button.save')}
            disabled={!value || !lessonPlanCodes[index]}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={data}
        pagination={{ pageSize: 99999 }}
        rowKey="prdCurriculumCode"
        rowClassName={(record: ISemesterEvaluation) =>
          record.evaluationCode ? '' : 'chosen'
        }
      />

      {(isLoading2 || isLoading3 || submitIsLoading || deleteIsLoading) && (
        <Loader/>
      )}
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={deleteError} />

    </>
  );
};

export default EvaluationNewTable;
